import { useEffect, useState } from "react";
import { reload } from "firebase/auth";
import { auth } from "../firebase";
import { logoutUser, sendVerificationEmailForUser } from "../authService";
import { useLanguage } from "../i18n/LanguageProvider";

const RESEND_COOLDOWN_SECONDS = 60;

function primaryButton(disabled = false) {
  return {
    width: "100%",
    minHeight: 58,
    borderRadius: 999,
    border: "2px solid rgba(255,255,255,0.30)",
    background: "linear-gradient(90deg, #ff007f 0%, #7a2bff 100%)",
    color: "white",
    fontWeight: 1000,
    fontSize: 15,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    boxShadow: disabled ? "none" : "0 18px 38px rgba(168,85,247,0.30)",
  };
}

function secondaryButton(disabled = false) {
  return {
    width: "100%",
    minHeight: 52,
    borderRadius: 999,
    border: "2px solid rgba(255,255,255,0.20)",
    background: "rgba(255,255,255,0.08)",
    color: "white",
    fontWeight: 1000,
    fontSize: 14,
    letterSpacing: 0.7,
    textTransform: "uppercase",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
  };
}

function EmailVerificationPage({ onVerified }) {
  const { t, language } = useLanguage();
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setCooldown((value) => Math.max(0, value - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [cooldown]);

  const showMessage = (text, type = "info") => {
    setMessage(text);
    setMessageType(type);
  };

  const handleCheckVerification = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      await logoutUser();
      return;
    }

    setChecking(true);
    try {
      await reload(currentUser);

      if (auth.currentUser?.emailVerified === true) {
        showMessage(t("emailVerification.messages.verified"), "success");
        onVerified?.();
        return;
      }

      showMessage(t("emailVerification.messages.notVerified"), "warning");
    } catch (error) {
      showMessage(t("emailVerification.messages.checkFailed"), "error");
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || sending) return;

    const currentUser = auth.currentUser;
    if (!currentUser) {
      await logoutUser();
      return;
    }

    setSending(true);
    try {
      await sendVerificationEmailForUser(currentUser, language);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      showMessage(t("emailVerification.messages.sent"), "success");
    } catch (error) {
      const code = String(error?.code || "");
      if (code.includes("too-many-requests")) {
        setCooldown(RESEND_COOLDOWN_SECONDS);
        showMessage(t("emailVerification.messages.tooManyRequests"), "warning");
      } else {
        showMessage(t("emailVerification.messages.sendFailed"), "error");
      }
    } finally {
      setSending(false);
    }
  };

  const handleLogout = async () => {
    await logoutUser();
  };

  const messageBackground =
    messageType === "success"
      ? "rgba(0,255,120,0.14)"
      : messageType === "error"
      ? "rgba(255,60,60,0.16)"
      : "rgba(255,255,255,0.10)";

  const messageBorder =
    messageType === "success"
      ? "rgba(0,255,120,0.45)"
      : messageType === "error"
      ? "rgba(255,60,60,0.48)"
      : "rgba(255,255,255,0.22)";

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        justifyContent: "center",
        alignItems: "stretch",
        background: "#000",
        padding: 0,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 430,
          minHeight: "100dvh",
          background: "linear-gradient(90deg, #080427 0%, #020047 100%)",
          color: "white",
          fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
          padding: "calc(34px + var(--vq-safe-area-top)) calc(22px + var(--vq-safe-area-right)) calc(34px + var(--vq-safe-area-bottom)) calc(22px + var(--vq-safe-area-left))",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 370,
            borderRadius: 28,
            padding: "28px 20px 22px",
            background: "linear-gradient(180deg, rgba(255,255,255,0.11), rgba(255,255,255,0.045))",
            border: "2px solid rgba(255,255,255,0.18)",
            boxShadow: "0 24px 70px rgba(0,0,0,0.38)",
            boxSizing: "border-box",
          }}
        >
          <img
            src="/vaultquest-logo.png"
            alt="VaultQuest"
            draggable="false"
            style={{
              width: 108,
              height: 108,
              objectFit: "contain",
              marginBottom: 18,
              filter: "drop-shadow(0 18px 38px rgba(168,85,247,0.36))",
            }}
          />

          <div
            style={{
              fontSize: 25,
              fontWeight: 1000,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              marginBottom: 12,
              lineHeight: 1.1,
            }}
          >
            {t("emailVerification.title")}
          </div>

          <div
            style={{
              fontSize: 15,
              lineHeight: 1.55,
              fontWeight: 800,
              color: "rgba(255,255,255,0.78)",
              marginBottom: 22,
            }}
          >
            {t("emailVerification.description")}
          </div>

          <div style={{ display: "grid", gap: 11 }}>
            <button
              type="button"
              onClick={handleCheckVerification}
              disabled={checking}
              style={primaryButton(checking)}
            >
              {checking
                ? t("emailVerification.buttons.checking")
                : t("emailVerification.buttons.check")}
            </button>

            <button
              type="button"
              onClick={handleResend}
              disabled={sending || cooldown > 0}
              style={secondaryButton(sending || cooldown > 0)}
            >
              {cooldown > 0
                ? t("emailVerification.buttons.resendCooldown", { seconds: cooldown })
                : sending
                ? t("emailVerification.buttons.sending")
                : t("emailVerification.buttons.resend")}
            </button>

            <button
              type="button"
              onClick={handleLogout}
              style={secondaryButton(false)}
            >
              {t("emailVerification.buttons.logout")}
            </button>
          </div>

          {message && (
            <div
              style={{
                marginTop: 16,
                padding: "12px 13px",
                borderRadius: 16,
                background: messageBackground,
                border: `1px solid ${messageBorder}`,
                color: "white",
                fontSize: 13,
                lineHeight: 1.4,
                fontWeight: 900,
              }}
            >
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EmailVerificationPage;
